import { useState, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams, Link } from 'react-router-dom'
import { format as dateFnsFormat } from 'date-fns'
import {
  ArrowLeft, Plus, Search, ChevronLeft, ChevronRight,
  RotateCcw, CheckCircle, Printer, X, Eye,
} from 'lucide-react'
import { PageHeader } from '../../components/ui/PageHeader'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { useCurrency } from '../../hooks/useCurrency'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { NotAuthorizedView } from '../../components/permissions/NotAuthorizedView'
import { supabase } from '../../lib/supabase'
import { getPaymentAccounts } from '../../lib/getPaymentAccounts'
import { fetchNonRefundPurchaseReturnsTotals, poOutstanding } from '../../lib/supabaseQueries'
import { displayDate } from '../../utils/dates'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

// ─── Types ─────────────────────────────────────────────────────────────────────

type ReturnType = 'credit_note' | 'refund_cash' | 'supplier_prepayment'

type PurchaseItem = {
  id: string
  product_id: string
  product_name: string
  quantity: number
  unit_price: number
  net_unit_cost: number | null
  total: number
}

type FoundPurchase = {
  id: string
  order_number: string
  waybill_number: string | null
  created_at: string
  total: number
  amount_paid: number
  payment_status: string
  status: string
  supplier_id: string | null
  suppliers: { id: string; name: string; business_name: string | null } | null
  purchase_order_items: PurchaseItem[]
}

type ReturnItem = {
  purchaseItemId: string
  productId: string
  productName: string
  originalQty: number
  returnQty: number
  unitCost: number
  selected: boolean
}

type ReturnRow = {
  id: string
  return_number: string
  debit_note_number: string | null
  supplier_name: string
  return_date: string
  reason: string
  total_amount: number
  status: string
  return_type: string | null
  journal_id: string | null
  original_purchase_id: string | null
  purchase_orders: { order_number: string } | null
  purchase_return_items?: Array<{
    id: string
    product_id: string
    quantity: number
    unit_cost: number
    total_amount: number
    products: { id: string; name: string; sku: string | null } | null
  }>
}

type ViewItem = {
  id: string
  product_name: string
  quantity: number
  unit_cost: number
  total_amount: number
}

type SuccessData = {
  debitNoteNumber: string
  totalAmount: number
  returnType: ReturnType
  supplierName: string
  purchaseOrderNumber: string
  refundAccountName: string
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20

const REASONS = [
  'Defective goods',
  'Wrong item delivered',
  'Damaged in transit',
  'Excess quantity',
  'Quality not as specified',
  'Other',
]

const TYPE_BADGE: Record<string, string> = {
  credit_note: 'badge-blue',
  refund_cash: 'badge-green',
  supplier_prepayment: 'badge-orange',
}
const TYPE_LABEL: Record<string, string> = {
  credit_note: 'Credit Note',
  refund_cash: 'Cash Refund',
  supplier_prepayment: 'Supplier Prepayment',
}
const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-gray',
  approved: 'badge-blue',
  completed: 'badge-green',
}

// ─── Debit Note Preview ───────────────────────────────────────────────────────

function DebitNotePreview({
  purchase,
  returnItems,
  reason,
  returnType,
  debitNoteNumber,
  returnDate,
  refundAccountName,
  totalAmount,
  businessName,
  format,
}: {
  purchase: FoundPurchase
  returnItems: ReturnItem[]
  reason: string
  returnType: ReturnType
  debitNoteNumber: string
  returnDate: string
  refundAccountName: string
  totalAmount: number
  businessName: string
  format: (n: number) => string
}) {
  const selected = returnItems.filter(i => i.selected && i.returnQty > 0)
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm font-sans space-y-5 print-target">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-bold text-lg text-gray-900">{businessName}</p>
          <p className="text-xs text-gray-400 mt-0.5">Purchase Returns Dept.</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-800 uppercase tracking-wider">Debit Note</p>
          <p className="font-mono text-brand-600 text-sm font-semibold">{debitNoteNumber}</p>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4 grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">To</p>
          <p className="font-semibold text-gray-900">{purchase.suppliers?.business_name ?? purchase.suppliers?.name ?? 'Unknown Supplier'}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Date</p>
          <p className="font-medium text-gray-900">{displayDate(returnDate)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Ref PO</p>
          <p className="font-mono text-gray-700">{purchase.order_number}</p>
        </div>
        {purchase.waybill_number && (
          <div className="text-right">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Ref Waybill</p>
            <p className="font-mono text-gray-700">{purchase.waybill_number}</p>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Items Returned</p>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              {['Product', 'Qty', 'Unit Cost', 'Total'].map(h => (
                <th
                  key={h}
                  className={cn(
                    'text-xs text-gray-500 pb-2 font-semibold',
                    h === 'Product' ? 'text-left' : 'text-right',
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {selected.map(item => (
              <tr key={item.purchaseItemId}>
                <td className="py-1.5 text-gray-900">{item.productName}</td>
                <td className="py-1.5 text-right text-gray-600">{item.returnQty}</td>
                <td className="py-1.5 text-right text-gray-600">{format(item.unitCost)}</td>
                <td className="py-1.5 text-right font-semibold text-gray-900">
                  {format(item.returnQty * item.unitCost)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
        <div>
          <p className="text-xs text-gray-400">Reason</p>
          <p className="text-sm text-gray-700 italic">{reason}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Total Return Value</p>
          <p className="text-2xl font-bold text-brand-600">{format(totalAmount)}</p>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-400">Settlement</p>
        <p className="text-sm font-medium text-gray-800">
          {returnType === 'credit_note' && 'Credit Note — offset against next purchase order'}
          {returnType === 'refund_cash' && `Cash Refund to ${refundAccountName || 'payment account'}`}
          {returnType === 'supplier_prepayment' && 'Supplier Prepayment Credit — available on next order'}
        </p>
      </div>

      <div className="border-t border-gray-100 pt-3 flex justify-between text-xs text-gray-400">
        <span>This is a machine-generated debit note.</span>
        <span>Authorised by system</span>
      </div>
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function PurchaseReturnsPage() {
  const { format } = useCurrency()
  const { tenant, user } = useAuthStore()
  const { viewScope, can } = usePermissions()
  const canReturn = can('purchases.return')
  const scope = viewScope('purchases')
  const ownerId = scope === 'own' ? (user?.id ?? '') : undefined
  const tid = tenant?.id ?? ''
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const prefilledPurchaseId = searchParams.get('purchaseId')

  const [view, setView] = useState<'list' | 'new' | 'success'>(
    prefilledPurchaseId ? 'new' : 'list',
  )

  // List state
  const [listSearch, setListSearch] = useState('')
  const [page, setPage] = useState(0)
  const [viewReturn, setViewReturn] = useState<ReturnRow | null>(null)
  const [viewItems, setViewItems] = useState<ViewItem[]>([])
  const [loadingViewItems, setLoadingViewItems] = useState(false)

  // Flow state
  const [step, setStep] = useState(prefilledPurchaseId ? 2 : 1)
  const [searchMode, setSearchMode] = useState<'po' | 'waybill'>('po')
  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState<FoundPurchase[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedPurchase, setSelectedPurchase] = useState<FoundPurchase | null>(null)
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([])
  const [selectedReason, setSelectedReason] = useState('')
  const [notes, setNotes] = useState('')
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10))
  const [returnType, setReturnType] = useState<ReturnType>('credit_note')
  const [refundAccountId, setRefundAccountId] = useState('')
  const [refundAccountName, setRefundAccountName] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [successData, setSuccessData] = useState<SuccessData | null>(null)
  const [returnedQtyMap, setReturnedQtyMap] = useState<Map<string, number>>(new Map())
  const [savedReturnRecord, setSavedReturnRecord] = useState<ReturnRow | null>(null)
  const [returnMap, setReturnMap] = useState<Map<string, number>>(new Map())
  const [nonRefundReturnMap, setNonRefundReturnMap] = useState<Map<string, number>>(new Map())
  const [prevNonRefundReturnsTotal, setPrevNonRefundReturnsTotal] = useState(0)

  // ── Load prefilled purchase ──────────────────────────────────────────────
  useEffect(() => {
    if (!prefilledPurchaseId || !tid) return
    loadPurchase(prefilledPurchaseId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledPurchaseId, tid])

  // ── Load previous return quantities for this PO (step 2) ─────────────────
  useEffect(() => {
    if (!selectedPurchase || step !== 2 || !tid) return
    async function loadPrevReturns() {
      const { data: prevReturns } = await db('purchase_returns')
        .select('id')
        .eq('tenant_id', tid)
        .eq('original_purchase_id', selectedPurchase!.id)
      const returnIds = (prevReturns ?? []).map((r: { id: string }) => r.id)
      if (!returnIds.length) { setReturnedQtyMap(new Map()); return }
      const { data: prevItems } = await db('purchase_return_items')
        .select('product_id, quantity')
        .eq('tenant_id', tid)
        .in('return_id', returnIds)
      const map = new Map<string, number>()
      for (const item of prevItems ?? []) {
        if (!item.product_id) continue
        map.set(item.product_id, (map.get(item.product_id) ?? 0) + Number(item.quantity))
      }
      setReturnedQtyMap(map)
    }
    loadPrevReturns()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPurchase?.id, step, tid])

  // ── Load prior non-refund (AP-reducing) returns total for balance preview ─
  useEffect(() => {
    if (!selectedPurchase || !tid) { setPrevNonRefundReturnsTotal(0); return }
    fetchNonRefundPurchaseReturnsTotals(tid, [selectedPurchase.id]).then(m => {
      setPrevNonRefundReturnsTotal(m[selectedPurchase.id] ?? 0)
    })
  }, [selectedPurchase, tid])

  // ── Returns list ─────────────────────────────────────────────────────────
  const { data: returns_ = [], isLoading: loadingList } = useQuery<ReturnRow[]>({
    queryKey: ['purchase-returns', tid, listSearch, page, ownerId],
    enabled: !!tid && view === 'list' && scope !== 'none',
    queryFn: async () => {
      let q = db('purchase_returns')
        .select(`
          id,
          debit_note_number,
          return_number,
          return_date,
          supplier_id,
          supplier_name,
          total_amount,
          return_type,
          status,
          original_purchase_id,
          reason,
          journal_id,
          created_at,
          purchase_orders!purchase_returns_original_purchase_id_fkey(order_number),
          purchase_return_items(id,product_id,quantity,unit_cost,total_amount,products(id,name,sku))
        `)
        .eq('tenant_id', tid)
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
      if (ownerId) q = q.eq('created_by', ownerId)
      if (listSearch) q = q.ilike('supplier_name', `%${listSearch}%`)
      const { data, error } = await q
      if (error) { if (String(error.message).includes('relation')) return []; throw error }
      return (data ?? []) as ReturnRow[]
    },
  })

  // ── Payment accounts for refund ──────────────────────────────────────────
  const { data: paymentAccounts = [] } = useQuery({
    queryKey: ['payment-accounts', tid],
    enabled: !!tid && step === 3,
    queryFn: () => getPaymentAccounts(supabase, tid),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────

  async function loadPurchase(purchaseId: string) {
    const { data, error } = await db('purchase_orders')
      .select(
        'id,order_number,waybill_number,created_at,total,amount_paid,payment_status,status,supplier_id,suppliers(id,name,business_name),purchase_order_items(id,product_id,product_name,quantity,unit_price,net_unit_cost,total)',
      )
      .eq('tenant_id', tid)
      .eq('id', purchaseId)
      .single()
    if (error || !data) { toast.error('Purchase not found'); return }
    setPurchaseAndItems(data as FoundPurchase)
  }

  function setPurchaseAndItems(po: FoundPurchase) {
    setSelectedPurchase(po)
    setReturnItems(
      (po.purchase_order_items ?? []).map(i => ({
        purchaseItemId: i.id,
        productId: i.product_id,
        productName: i.product_name,
        originalQty: i.quantity,
        returnQty: 0,
        unitCost: i.net_unit_cost || i.unit_price || 0,
        selected: false,
      })),
    )
    setStep(2)
  }

  const doSearch = useCallback(async () => {
    if (!searchInput.trim()) return
    setSearching(true)
    try {
      const field = searchMode === 'po' ? 'order_number' : 'waybill_number'
      const { data } = await db('purchase_orders')
        .select(
          'id,order_number,waybill_number,created_at,total,amount_paid,payment_status,status,supplier_id,suppliers(id,name,business_name),purchase_order_items(id,product_id,product_name,quantity,unit_price,net_unit_cost,total)',
        )
        .eq('tenant_id', tid)
        .ilike(field, `%${searchInput}%`)
        .neq('status', 'cancelled')
        .limit(5)
      const results = (data ?? []) as FoundPurchase[]
      setSearchResults(results)

      if (results.length > 0) {
        const poIds = results.map(po => po.id)
        const { data: returnTotals } = await db('purchase_returns')
          .select('original_purchase_id,total_amount')
          .eq('tenant_id', tid)
          .in('original_purchase_id', poIds)
        const map = new Map<string, number>()
        for (const r of returnTotals ?? []) {
          const current = map.get(r.original_purchase_id) ?? 0
          map.set(r.original_purchase_id, current + Number(r.total_amount))
        }
        setReturnMap(map)

        const nonRefund = await fetchNonRefundPurchaseReturnsTotals(tid, poIds)
        const nrMap = new Map<string, number>()
        for (const [poId, total] of Object.entries(nonRefund)) nrMap.set(poId, total)
        setNonRefundReturnMap(nrMap)
      } else {
        setReturnMap(new Map())
        setNonRefundReturnMap(new Map())
      }
    } finally {
      setSearching(false)
    }
  }, [searchInput, searchMode, tid])

  function resetFlow() {
    setStep(1)
    setSearchInput('')
    setSearchResults([])
    setSelectedPurchase(null)
    setReturnItems([])
    setSelectedReason('')
    setNotes('')
    setReturnDate(new Date().toISOString().slice(0, 10))
    setReturnType('credit_note')
    setRefundAccountId('')
    setRefundAccountName('')
    setSuccessData(null)
    setSavedReturnRecord(null)
    setReturnedQtyMap(new Map())
  }

  function goToList() {
    resetFlow()
    setView('list')
  }

  const selectedItems = returnItems.filter(i => i.selected && i.returnQty > 0)
  const totalReturnValue = selectedItems.reduce((s, i) => s + i.returnQty * i.unitCost, 0)

  async function loadViewItems(returnId: string) {
    setLoadingViewItems(true)
    try {
      const { data } = await db('purchase_return_items')
        .select('id,product_name,quantity,unit_cost,total_amount')
        .eq('return_id', returnId)
        .eq('tenant_id', tid)
      setViewItems((data ?? []) as ViewItem[])
    } finally {
      setLoadingViewItems(false)
    }
  }

  function handlePrintDebitNote(ret: ReturnRow, items: ViewItem[]) {
    const itemRows = items.map(item => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee">${item.product_name || 'Product'}</td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:right">${item.quantity}</td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:right">&#8358;${Number(item.unit_cost || 0).toLocaleString()}</td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:right">&#8358;${Number(item.total_amount || 0).toLocaleString()}</td>
      </tr>
    `).join('')

    const html = `<!DOCTYPE html>
<html>
<head>
<title>Debit Note ${ret.debit_note_number}</title>
<style>
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 20mm; }
.header { text-align: center; margin-bottom: 20px; }
.co { font-size: 20px; font-weight: bold; color: #f97316; }
.title { font-size: 14px; font-weight: bold; margin-top: 4px; letter-spacing: 2px; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
.lbl { font-size: 10px; color: #666; text-transform: uppercase; margin-bottom: 2px; }
.val { font-size: 12px; font-weight: 500; }
table { width: 100%; border-collapse: collapse; margin: 20px 0; }
th { background: #f97316; color: white; padding: 8px; text-align: left; font-size: 11px; }
th:not(:first-child) { text-align: right; }
.tot { font-weight: bold; border-top: 2px solid #111; }
.note { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 4px; padding: 10px; margin: 10px 0; font-size: 11px; }
.sigs { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
.sig { border-top: 1px solid #111; padding-top: 5px; font-size: 10px; color: #666; margin-top: 40px; }
</style>
</head>
<body>
<div class="header">
  <div class="co">${tenant?.name || 'Company'}</div>
  <div class="title">DEBIT NOTE</div>
</div>
<div class="grid">
  <div>
    <div class="lbl">Debit Note #</div>
    <div class="val">${ret.debit_note_number}</div>
    <div class="lbl" style="margin-top:12px">Date</div>
    <div class="val">${new Date(ret.return_date).toLocaleDateString('en-NG', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
  </div>
  <div>
    <div class="lbl">To (Supplier)</div>
    <div class="val">${ret.supplier_name || '&#8212;'}</div>
    <div class="lbl" style="margin-top:12px">Original PO</div>
    <div class="val">${ret.purchase_orders?.order_number || '&#8212;'}</div>
  </div>
</div>
<div class="note">We hereby debit your account for the return of the following goods. Please issue a credit note or arrange a refund accordingly.</div>
<table>
  <thead>
    <tr>
      <th>Product</th>
      <th style="text-align:right">Qty</th>
      <th style="text-align:right">Unit Cost</th>
      <th style="text-align:right">Total</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows}
    <tr class="tot">
      <td style="padding:8px" colspan="3">Total</td>
      <td style="padding:8px;text-align:right">&#8358;${Number(ret.total_amount).toLocaleString()}</td>
    </tr>
  </tbody>
</table>
<div class="sigs">
  <div><div class="sig">Prepared by</div></div>
  <div><div class="sig">Authorised by</div></div>
</div>
</body>
</html>`

    const w = window.open('', '_blank', 'width=800,height=600')
    if (!w) {
      toast.error('Allow popups to print')
      return
    }
    w.document.write(html)
    w.document.close()
    w.focus()
    setTimeout(() => { w.print() }, 600)
  }

  // ── Confirm return ────────────────────────────────────────────────────────────
  // Atomic: routed through post_purchase_return_atomic (migration 063).
  // Single transaction handles header + items + stock + journal + supplier balance.
  // 'credit_note' and 'supplier_prepayment' both map to refund_method='credit'
  // — the new model uses a single signed AP balance per supplier (no separate
  // Supplier Prepayments account). 'refund_cash' maps to refund_method='cash'.
  async function confirmReturn() {
    if (!canReturn) { toast.error('Not authorized to process purchase returns'); return }
    if (!selectedPurchase) return
    if (!selectedReason) { toast.error('Please select a return reason'); return }
    if (selectedItems.length === 0) { toast.error('Select at least one item to return'); return }
    if (returnType === 'refund_cash' && !refundAccountId) {
      toast.error('Select a refund account')
      return
    }

    setIsProcessing(true)
    try {
      const supplierName = selectedPurchase.suppliers?.business_name || selectedPurchase.suppliers?.name || 'Supplier'
      const refundMethod = returnType === 'refund_cash' ? 'cash' : 'credit'

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('post_purchase_return_atomic', {
        p_tenant_id: tid,
        p_purchase_order_id: selectedPurchase.id,
        p_return_date: returnDate,
        p_reason: selectedReason + (notes ? ` — ${notes}` : ''),
        p_refund_method: refundMethod,
        p_items: selectedItems.map(i => ({
          product_id: i.productId,
          product_name: i.productName,
          quantity: i.returnQty,
          unit_cost: i.unitCost,
        })),
        p_refund_account_id: refundMethod === 'cash' ? refundAccountId : null,
        p_notes: notes || null,
        p_created_by: user?.id ?? null,
      })
      if (error) throw new Error(error.message)

      const result = data as {
        return_id: string
        return_number: string
        journal_id: string | null
        total_amount: number
      }

      setSuccessData({
        debitNoteNumber: result.return_number,
        totalAmount: result.total_amount,
        returnType,
        supplierName,
        purchaseOrderNumber: selectedPurchase.order_number,
        refundAccountName,
      })
      setSavedReturnRecord({
        id: result.return_id,
        return_number: result.return_number,
        debit_note_number: result.return_number,
        supplier_name: supplierName,
        return_date: returnDate,
        reason: selectedReason + (notes ? ` — ${notes}` : ''),
        total_amount: result.total_amount,
        status: 'completed',
        return_type: returnType,
        journal_id: result.journal_id,
        original_purchase_id: selectedPurchase.id,
        purchase_orders: { order_number: selectedPurchase.order_number },
      })
      qc.invalidateQueries({ queryKey: ['purchase-returns', tid] })
      qc.invalidateQueries({ queryKey: ['purchase-orders-enhanced'] })
      qc.invalidateQueries({ queryKey: ['supplier-balances', tid] })
      setView('success')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to process return')
    } finally {
      setIsProcessing(false)
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SUCCESS SCREEN
  // ─────────────────────────────────────────────────────────────────────────
  if (scope === 'none') return <NotAuthorizedView />

  if (view === 'success' && successData) {
    return (
      <div className="max-w-lg mx-auto py-12">
        <div className="card text-center py-10 px-6 space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Return Confirmed!</h3>
          <p className="text-gray-500">{successData.debitNoteNumber}</p>
          <p className="text-sm text-gray-500">
            {successData.returnType === 'refund_cash' ? (
              <>
                <span className="font-semibold text-gray-800">{successData.refundAccountName || 'Refund account'}</span>
                {' '}has been credited with{' '}
                <span className="font-semibold text-gray-800">{format(successData.totalAmount)}</span>
              </>
            ) : (
              <>
                {successData.supplierName}'s outstanding balance has been reduced by{' '}
                <span className="font-semibold text-gray-800">{format(successData.totalAmount)}</span>
              </>
            )}
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <button
              onClick={() => {
                if (!savedReturnRecord) return
                const items: ViewItem[] = selectedItems.map(item => ({
                  id: item.purchaseItemId,
                  product_name: item.productName,
                  quantity: item.returnQty,
                  unit_cost: item.unitCost,
                  total_amount: item.returnQty * item.unitCost,
                }))
                handlePrintDebitNote(savedReturnRecord, items)
              }}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600"
            >
              🖨 Print Debit Note
            </button>
            <button
              onClick={goToList}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              View Returns List
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // NEW RETURN FLOW
  // ─────────────────────────────────────────────────────────────────────────
  if (view === 'new') {
    const isPaid = selectedPurchase?.payment_status === 'paid' || selectedPurchase?.payment_status === 'partial'
    const supplierLabel = selectedPurchase?.suppliers?.business_name || selectedPurchase?.suppliers?.name || 'Supplier'
    const currentBalance = selectedPurchase
      ? poOutstanding({
          total: selectedPurchase.total,
          amount_paid: selectedPurchase.amount_paid,
          non_refund_returns_total: prevNonRefundReturnsTotal,
          status: selectedPurchase.status,
        })
      : 0
    const afterReturnBalance = selectedPurchase
      ? poOutstanding({
          total: selectedPurchase.total,
          amount_paid: selectedPurchase.amount_paid,
          non_refund_returns_total: prevNonRefundReturnsTotal + totalReturnValue,
          status: selectedPurchase.status,
        })
      : 0

    return (
      <div className="max-w-3xl mx-auto space-y-5 pb-8">
        <style>{`@media print { .no-print { display: none !important; } }`}</style>

        {/* Header */}
        <div className="no-print flex items-center gap-3">
          <button onClick={goToList} className="p-2 rounded-xl hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">New Purchase Return</h1>
        </div>

        {/* Step indicators — 3 steps */}
        <div className="no-print flex items-center gap-2">
          {[
            { n: 1, label: 'Find Purchase' },
            { n: 2, label: 'Select Items' },
            { n: 3, label: 'Review & Confirm' },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors',
                step === s.n ? 'bg-brand-600 text-white' :
                step > s.n ? 'bg-green-500 text-white' :
                'bg-gray-200 text-gray-500',
              )}>{step > s.n ? '✓' : s.n}</div>
              <span className={cn('text-xs hidden sm:block', step === s.n ? 'font-semibold text-gray-900' : 'text-gray-400')}>{s.label}</span>
              {i < 2 && <div className={cn('h-0.5 w-6', step > s.n ? 'bg-green-400' : 'bg-gray-200')} />}
            </div>
          ))}
        </div>

        {/* ── STEP 1: Find Purchase ── */}
        {step === 1 && (
          <div className="card space-y-5">
            <h2 className="font-semibold text-gray-800">Find the original purchase</h2>

            <div className="flex gap-2 border-b border-gray-100 pb-3">
              {(['po', 'waybill'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => { setSearchMode(m); setSearchInput(''); setSearchResults([]) }}
                  className={cn(
                    'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    searchMode === m ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  )}
                >
                  {m === 'po' ? 'Search by PO Number' : 'Search by Waybill Number'}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  className="input pl-9"
                  placeholder={searchMode === 'po' ? 'Enter PO reference…' : 'Enter waybill number…'}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doSearch()}
                />
              </div>
              <button className="btn-primary" onClick={doSearch} disabled={searching}>
                {searching ? <Spinner size="sm" /> : 'Search'}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left font-medium text-gray-600">PO Number</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Supplier</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Items</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Original</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Returned</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Outstanding</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Payment</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(po => {
                      const returned = returnMap.get(po.id) ?? 0
                      const nonRefundReturned = nonRefundReturnMap.get(po.id) ?? 0
                      const original = po.total + returned
                      const outstanding = poOutstanding({
                        total: po.total,
                        amount_paid: po.amount_paid,
                        non_refund_returns_total: nonRefundReturned,
                        status: po.status,
                      })
                      return (
                        <tr key={po.id} className="border-b border-gray-100 hover:bg-orange-50/30 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900 font-mono">{po.order_number}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {dateFnsFormat(new Date(po.created_at), 'dd MMM yyyy')}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {po.suppliers?.business_name || po.suppliers?.name || 'Unknown'}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            {po.purchase_order_items?.length || 0}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-400 font-medium">
                            {format(original)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {returned > 0
                              ? <span className="text-red-600">{format(returned)}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            <span className={outstanding <= 0 ? 'text-green-600' : 'text-red-600'}>
                              {format(Math.max(0, outstanding))}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={cn(
                              'px-2 py-1 rounded-full text-xs font-medium',
                              po.payment_status === 'paid' ? 'bg-green-50 text-green-700' :
                              po.payment_status === 'partial' ? 'bg-amber-50 text-amber-700' :
                              'bg-red-50 text-red-600',
                            )}>
                              {po.payment_status === 'paid' ? 'PAID ✓' : po.payment_status === 'partial' ? 'PARTIAL' : 'UNPAID'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => setPurchaseAndItems(po)}
                              disabled={po.status === 'cancelled'}
                              title={po.status === 'cancelled' ? 'Cannot return a cancelled PO' : undefined}
                              className={cn(
                                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                                po.status === 'cancelled'
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-orange-500 text-white hover:bg-orange-600',
                              )}
                            >
                              Select →
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {searchResults.length === 0 && searchInput && !searching && (
              <p className="text-sm text-gray-400 text-center py-4">No purchases found. Try a different search term.</p>
            )}
          </div>
        )}

        {/* ── STEP 2: Select Items ── */}
        {step === 2 && selectedPurchase && (
          <div className="card space-y-5">
            <div className="flex items-start justify-between">
              <h2 className="font-semibold text-gray-800">Select items to return</h2>
              <button className="text-xs text-brand-600 hover:underline" onClick={() => setReturnItems(prev => prev.map(i => ({ ...i, selected: true, returnQty: i.returnQty || i.originalQty })))}>
                Select All
              </button>
            </div>

            <div className="rounded-xl bg-gray-50 p-3 text-sm grid grid-cols-2 gap-2">
              <div><span className="text-gray-400">PO:</span> <strong className="font-mono">{selectedPurchase.order_number}</strong></div>
              <div><span className="text-gray-400">Supplier:</span> <strong>{selectedPurchase.suppliers?.business_name ?? selectedPurchase.suppliers?.name ?? '—'}</strong></div>
              {selectedPurchase.waybill_number && (
                <div><span className="text-gray-400">Waybill:</span> <span className="font-mono">{selectedPurchase.waybill_number}</span></div>
              )}
              <div><span className="text-gray-400">Total:</span> <strong>{format(selectedPurchase.total)}</strong></div>
            </div>

            <div className="overflow-x-auto">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th className="w-7"></th>
                    <th>Product</th>
                    <th className="text-center whitespace-nowrap">Ordered</th>
                    <th className="text-center whitespace-nowrap">Available</th>
                    <th className="text-center whitespace-nowrap">Return Qty</th>
                    <th className="text-right whitespace-nowrap">Net Cost</th>
                    <th className="text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {returnItems.map((item, i) => {
                    const alreadyReturned = returnedQtyMap.get(item.productId) ?? 0
                    const availableToReturn = item.originalQty - alreadyReturned
                    const isFullyReturned = availableToReturn <= 0
                    return (
                      <tr key={item.purchaseItemId} className={cn(item.selected ? 'bg-brand-50/30' : '', isFullyReturned ? 'opacity-50' : '')}>
                        <td>
                          <input
                            type="checkbox"
                            checked={item.selected}
                            disabled={isFullyReturned}
                            onChange={e =>
                              setReturnItems(prev =>
                                prev.map((it, idx) =>
                                  idx === i ? { ...it, selected: e.target.checked, returnQty: e.target.checked ? (it.returnQty || Math.min(it.originalQty, availableToReturn)) : 0 } : it,
                                ),
                              )
                            }
                            className="h-3.5 w-3.5 rounded text-brand-600"
                          />
                        </td>
                        <td className="font-medium text-gray-900">
                          {item.productName}
                          {isFullyReturned && (
                            <span className="ml-2 text-[10px] text-gray-500">(Fully returned)</span>
                          )}
                          {alreadyReturned > 0 && !isFullyReturned && (
                            <span className="ml-2 text-[10px] text-gray-400">{alreadyReturned} returned</span>
                          )}
                        </td>
                        <td className="text-center text-gray-500">{item.originalQty}</td>
                        <td className="text-center">
                          <span className={cn('font-medium', availableToReturn > 0 ? 'text-green-700' : 'text-gray-400')}>
                            {availableToReturn}
                          </span>
                        </td>
                        <td>
                          <div className={cn('flex items-center gap-px justify-center', (!item.selected || isFullyReturned) && 'opacity-40')}>
                            <button
                              type="button"
                              disabled={!item.selected || isFullyReturned}
                              onClick={() => {
                                const v = Math.max(0, item.returnQty - 1)
                                setReturnItems(prev => prev.map((it, idx) => idx === i ? { ...it, returnQty: v } : it))
                              }}
                              className="w-6 h-[23px] rounded-l border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:pointer-events-none font-bold text-sm select-none"
                            >
                              −
                            </button>
                            <input
                              type="number"
                              min={0}
                              max={availableToReturn}
                              disabled={!item.selected || isFullyReturned}
                              value={item.returnQty}
                              onChange={e => {
                                const raw = parseInt(e.target.value)
                                if (Number.isNaN(raw)) {
                                  setReturnItems(prev => prev.map((it, idx) => idx === i ? { ...it, returnQty: 0 } : it))
                                  return
                                }
                                if (raw > availableToReturn) {
                                  toast.error(`Max returnable quantity is ${availableToReturn}`)
                                  setReturnItems(prev => prev.map((it, idx) => idx === i ? { ...it, returnQty: availableToReturn } : it))
                                  return
                                }
                                const v = Math.max(0, raw)
                                setReturnItems(prev => prev.map((it, idx) => idx === i ? { ...it, returnQty: v } : it))
                              }}
                              className="w-12 h-[23px] text-center border-y border-gray-200 text-sm focus:ring-1 focus:ring-orange-400 outline-none disabled:pointer-events-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <button
                              type="button"
                              disabled={!item.selected || isFullyReturned}
                              onClick={() => {
                                const v = Math.min(availableToReturn, item.returnQty + 1)
                                setReturnItems(prev => prev.map((it, idx) => idx === i ? { ...it, returnQty: v } : it))
                              }}
                              className="w-6 h-[23px] rounded-r border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:pointer-events-none font-bold text-sm select-none"
                            >
                              +
                            </button>
                          </div>
                          {item.selected && item.returnQty === 0 && !isFullyReturned && (
                            <p className="text-[10px] text-amber-600 text-center mt-0.5">Quantity required</p>
                          )}
                        </td>
                        <td className="text-right text-gray-600">{format(item.unitCost)}</td>
                        <td className="text-right font-semibold text-brand-700">
                          {item.returnQty > 0 ? format(item.returnQty * item.unitCost) : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div>
              <label className="label">Return Reason *</label>
              <div className="flex flex-wrap gap-2">
                {REASONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setSelectedReason(r)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                      selectedReason === r
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300',
                    )}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Notes (optional)</label>
              <textarea
                className="input resize-none"
                rows={2}
                placeholder="Additional details about the return…"
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Return Value</span>
              <span className="text-xl font-bold text-amber-700">{format(totalReturnValue)}</span>
            </div>

            <div className="flex justify-between">
              <button className="btn-secondary" onClick={() => { setSelectedPurchase(null); setReturnItems([]); setStep(1) }}>
                ← Back
              </button>
              <button
                className="btn-primary"
                disabled={selectedItems.length === 0 || !selectedReason}
                onClick={() => setStep(3)}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Review & Confirm ── */}
        {step === 3 && selectedPurchase && (
          <div className="space-y-5">
            {/* Return type selection */}
            <div className="card space-y-4">
              <h2 className="font-semibold text-gray-800">How should this return be handled?</h2>

              <div className="space-y-3">
                {/* Credit Note — always available */}
                <label className={cn(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors',
                  returnType === 'credit_note' ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300',
                )}>
                  <input
                    type="radio"
                    className="mt-0.5 h-4 w-4 text-brand-600"
                    checked={returnType === 'credit_note'}
                    onChange={() => setReturnType('credit_note')}
                  />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Credit Note</p>
                    <p className="text-xs text-gray-500 mt-0.5">Reduces what we owe the supplier. No money moves.</p>
                  </div>
                </label>

                {/* Cash Refund — only if paid or partial */}
                {isPaid && (
                  <label className={cn(
                    'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors',
                    returnType === 'refund_cash' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300',
                  )}>
                    <input
                      type="radio"
                      className="mt-0.5 h-4 w-4 text-green-600"
                      checked={returnType === 'refund_cash'}
                      onChange={() => setReturnType('refund_cash')}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm">Cash Refund</p>
                      <p className="text-xs text-gray-500 mt-0.5">Supplier refunds us in cash/bank. Select refund account below.</p>
                      {returnType === 'refund_cash' && (
                        <div className="mt-3">
                          <label className="label text-xs">Refund Account *</label>
                          <select
                            className="input text-sm"
                            value={refundAccountId}
                            onChange={e => {
                              setRefundAccountId(e.target.value)
                              const acct = paymentAccounts.find(a => a.id === e.target.value)
                              setRefundAccountName(acct?.name ?? '')
                            }}
                          >
                            <option value="">— Select account —</option>
                            {paymentAccounts.map(a => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Supplier balance preview for credit_note */}
            {returnType === 'credit_note' && (
              <div className="card space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Effect on supplier account</p>
                <div className="rounded-xl border border-gray-200 p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{supplierLabel} current balance:</span>
                    <span className="font-semibold">{format(currentBalance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">After this return:</span>
                    <span className="font-semibold text-green-700">{format(afterReturnBalance)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-100 pt-2">
                    <span className="text-gray-500">Balance reduces by:</span>
                    <span className="font-bold text-brand-600">{format(totalReturnValue)}</span>
                  </div>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 font-mono text-xs text-gray-600 space-y-1">
                  <div className="flex justify-between"><span>DR Accounts Payable</span><span>{format(totalReturnValue)}</span></div>
                  <div className="flex justify-between"><span>CR Stock on Hand</span><span>{format(totalReturnValue)}</span></div>
                </div>
              </div>
            )}

            {/* Journal preview for cash refund */}
            {returnType === 'refund_cash' && (
              <div className="card space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Journal Preview</p>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 font-mono text-xs text-gray-600 space-y-1">
                  <div className="flex justify-between"><span>DR {refundAccountName || '[Selected Account]'}</span><span>{format(totalReturnValue)}</span></div>
                  <div className="flex justify-between"><span>CR Stock on Hand</span><span>{format(totalReturnValue)}</span></div>
                </div>
              </div>
            )}

            {/* Return date */}
            <div className="card">
              <div className="max-w-xs">
                <label className="label">Return Date *</label>
                <input
                  type="date"
                  className="input"
                  value={returnDate}
                  onChange={e => setReturnDate(e.target.value)}
                />
              </div>
            </div>

            {/* Debit note preview */}
            <div className="card space-y-3">
              <h2 className="font-semibold text-gray-800">Review Debit Note</h2>
              <p className="text-xs text-gray-400">This is a preview. The debit note number will be generated on confirmation.</p>
            </div>

            <DebitNotePreview
              purchase={selectedPurchase}
              returnItems={returnItems}
              reason={selectedReason}
              returnType={returnType}
              debitNoteNumber="DBN-XXXX-XXXX (preview)"
              returnDate={returnDate}
              refundAccountName={refundAccountName}
              totalAmount={totalReturnValue}
              businessName={tenant?.name ?? 'Business'}
              format={format}
            />

            <div className="flex justify-between">
              <button className="btn-secondary" onClick={() => setStep(2)}>← Back</button>
              {canReturn ? (
                <button
                  className="btn-primary flex items-center gap-2"
                  onClick={confirmReturn}
                  disabled={isProcessing}
                >
                  {isProcessing ? <><Spinner size="sm" /> Processing…</> : 'Confirm Return'}
                </button>
              ) : (
                <p className="text-xs text-gray-400 self-center">You don't have permission to process returns</p>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RETURNS LIST
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/purchases" className="p-2 rounded-xl hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <PageHeader
          title="Purchase Returns"
          actions={
            <button
              className="btn-primary flex items-center gap-2"
              onClick={() => { resetFlow(); setView('new') }}
            >
              <Plus className="h-4 w-4" /> New Return
            </button>
          }
        />
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          className="input pl-9"
          placeholder="Search by supplier…"
          value={listSearch}
          onChange={e => { setListSearch(e.target.value); setPage(0) }}
        />
        {listSearch && (
          <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setListSearch('')}>
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        {loadingList ? (
          <div className="p-8 flex justify-center"><Spinner /></div>
        ) : returns_.length === 0 ? (
          <EmptyState
            icon={<RotateCcw className="h-10 w-10 text-gray-300" />}
            title="No purchase returns yet"
            description="Return goods to suppliers using the New Return button above."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['DBN #', 'Date', 'Supplier', 'PO Ref', 'Total', 'Type', 'Status', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {returns_.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs font-medium text-brand-600">{r.debit_note_number ?? r.return_number}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{displayDate(r.return_date)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.supplier_name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.purchase_orders?.order_number ?? '—'}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{format(r.total_amount)}</td>
                      <td className="px-4 py-3">
                        {r.return_type ? (
                          <span className={cn('badge', TYPE_BADGE[r.return_type] ?? 'badge-gray')}>
                            {TYPE_LABEL[r.return_type] ?? r.return_type}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('badge', STATUS_BADGE[r.status] ?? 'badge-gray')}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          className="text-xs text-brand-600 hover:underline flex items-center gap-1"
                          onClick={() => {
                            setViewReturn(r)
                            if (r.purchase_return_items?.length) {
                              setViewItems(r.purchase_return_items.map(it => ({
                                id: it.id,
                                product_name: it.products?.name ?? 'Product',
                                quantity: it.quantity,
                                unit_cost: it.unit_cost,
                                total_amount: it.total_amount,
                              })))
                            } else {
                              setViewItems([])
                              loadViewItems(r.id)
                            }
                          }}
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Page {page + 1}</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="btn-secondary p-1.5 disabled:opacity-40">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button disabled={returns_.length < PAGE_SIZE} onClick={() => setPage(p => p + 1)} className="btn-secondary p-1.5 disabled:opacity-40">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── View Return Detail Modal ── */}
      {viewReturn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="font-bold text-gray-900">Debit Note Detail</h2>
                <p className="text-xs text-brand-600 font-mono mt-0.5">{viewReturn.debit_note_number ?? viewReturn.return_number}</p>
              </div>
              <button onClick={() => setViewReturn(null)} className="p-2 rounded-xl hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Summary grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Supplier</p>
                  <p className="font-semibold text-gray-900">{viewReturn.supplier_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Date</p>
                  <p className="font-medium text-gray-900">{displayDate(viewReturn.return_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Original PO</p>
                  <p className="font-mono text-gray-700">{viewReturn.purchase_orders?.order_number ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Return Type</p>
                  {viewReturn.return_type ? (
                    <span className={cn('badge', TYPE_BADGE[viewReturn.return_type] ?? 'badge-gray')}>
                      {TYPE_LABEL[viewReturn.return_type] ?? viewReturn.return_type}
                    </span>
                  ) : <span className="text-gray-400">—</span>}
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Total Amount</p>
                  <p className="font-bold text-gray-900">{format(viewReturn.total_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Journal Entry</p>
                  <p className={cn('text-sm font-medium', viewReturn.journal_id ? 'text-green-600' : 'text-amber-600')}>
                    {viewReturn.journal_id ? 'Posted ✓' : 'Not posted'}
                  </p>
                </div>
              </div>

              {/* Reason */}
              {viewReturn.reason && (
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Reason</p>
                  <p className="text-sm text-gray-700 italic">{viewReturn.reason}</p>
                </div>
              )}

              {/* Items table */}
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Items Returned</p>
                {loadingViewItems ? (
                  <div className="flex justify-center py-4"><Spinner /></div>
                ) : viewItems.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-3">No items found.</p>
                ) : (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Product</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Unit Cost</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {viewItems.map(item => (
                          <tr key={item.id}>
                            <td className="px-4 py-2 text-gray-900">{item.product_name}</td>
                            <td className="px-4 py-2 text-right text-gray-600">{item.quantity}</td>
                            <td className="px-4 py-2 text-right text-gray-600">{format(item.unit_cost)}</td>
                            <td className="px-4 py-2 text-right font-semibold text-gray-900">{format(item.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 pb-5 flex justify-between items-center">
              <button className="btn-secondary" onClick={() => setViewReturn(null)}>Close</button>
              <button
                className="btn-primary flex items-center gap-2"
                onClick={() => handlePrintDebitNote(viewReturn, viewItems)}
                disabled={viewItems.length === 0}
              >
                <Printer className="h-4 w-4" /> Print Debit Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
